import type {
  FunctionComponent,
  HTMLProps,
  KeyboardEvent,
  MouseEvent,
} from 'react'
import React, { useEffect, useRef, useState } from 'react'

import classnames from 'classnames'
import FocusTrap from 'focus-trap-react'

import { PadlockIcon } from '../Icons/Miscellaneous/Padlock'
import { ChevronDownIcon } from '../Icons/Navigational/ChevronDown'
import { ChevronUpIcon } from '../Icons/Navigational/ChevronUp'
import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import styles from './CustomDropdown.module.scss'

export const CustomDropdown: FunctionComponent<CustomDropdownProps> = ({
  className,
  classNameSelectedContainer,
  classNameOptionsContainer,
  options,
  handleSortByCallback,
  handleOptionWithIconCallback,
  handleOpenCloseCallback,
  defaultOption,
  tracking,
  ...rest
}) => {
  const optionsHaveTracking = tracking && Object.keys(tracking).length

  const preSelectedOption =
    defaultOption && defaultOption >= 0 && defaultOption < options.length
      ? defaultOption
      : 0

  const [showOptions, setShowOptions] = useState<boolean>(false)

  const [selectedOption, setSelectedOption] = useState<Option>(
    options[preSelectedOption]
  )

  const optionsContainerRef = useRef<HTMLUListElement>(null)
  const selectedContainerRef = useRef<HTMLDivElement>(null)

  const handleShowOptions = (event: KeyboardEvent | MouseEvent) => {
    const toggleOptions =
      ('key' in event && event.key === 'Enter') || event.type === 'click'

    if (toggleOptions) {
      setShowOptions((prev) => {
        if (typeof handleOpenCloseCallback === 'function') {
          handleOpenCloseCallback({ showOptions: !prev })
        }
        return !prev
      })
    }
  }

  const handleOptionClick = ({ label, value, icon }: Option) => {
    if (typeof handleOptionWithIconCallback === 'function' && icon) {
      handleOptionWithIconCallback({ label, value, icon })
      return
    }

    setSelectedOption({ label, value, icon })
    setShowOptions(false)
    if (typeof handleSortByCallback === 'function') {
      handleSortByCallback({ label, value })
    }
  }

  const handleClickOutsideOptionsContainer = (e: globalThis.MouseEvent) => {
    if (
      /* istanbul ignore next */
      optionsContainerRef.current?.contains(e.target as Node) ||
      selectedContainerRef.current?.contains(e.target as Node)
    ) {
      return
    } else if (showOptions) {
      setShowOptions(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutsideOptionsContainer)
    return () => {
      document.removeEventListener(
        'mousedown',
        handleClickOutsideOptionsContainer
      )
    }
  })

  return (
    <div
      data-testid="drop-down-container"
      className={classnames(styles.dropDownContainer, className)}
      {...rest}
    >
      <div
        data-testid="selected-container"
        className={classnames(
          classNameSelectedContainer,
          styles.selectedContainer,
          showOptions && styles.activeDropdown
        )}
        onClick={handleShowOptions}
        onKeyPress={handleShowOptions}
        tabIndex={0}
        role="button"
        aria-label={`click to view drop down menu, sorted by ${selectedOption.label}`}
        ref={selectedContainerRef}
      >
        <div
          className={styles.selectedOption}
          {...(optionsHaveTracking && {
            [tracking?.label]: `${tracking?.id}-option-0`,
          })}
        >
          {selectedOption.label}
        </div>
        {showOptions ? (
          <ChevronUpIcon
            className={styles.chevronIcon}
            {...(optionsHaveTracking && {
              [tracking?.label]: `${tracking?.id}-option-0`,
            })}
          />
        ) : (
          <ChevronDownIcon
            className={styles.chevronIcon}
            {...(optionsHaveTracking && {
              [tracking?.label]: `${tracking?.id}-option-0`,
            })}
          />
        )}
      </div>

      {showOptions && (
        <FocusTrap
          focusTrapOptions={{
            clickOutsideDeactivates: true,
            initialFocus: `li[data-value="${selectedOption.value}"]`,
            tabbableOptions: {
              displayCheck: 'none',
            },
          }}
        >
          <div
            className={classnames(
              classNameOptionsContainer,
              styles.dropDownWrapper
            )}
          >
            <ul
              data-testid="options-container"
              className={styles.optionsWrapper}
              ref={optionsContainerRef}
            >
              {options.map(({ label, value, icon }, index) => {
                return (
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                  <li
                    data-testid={`option ${value}`}
                    data-value={value}
                    key={`${value}-${index}`}
                    className={classnames(
                      selectedOption.label === label && styles.optionSelected,
                      styles.option
                    )}
                    onClick={() => {
                      handleOptionClick({ label, value, icon })
                    }}
                    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                    tabIndex={0}
                    onKeyPress={(event) => {
                      event.key === 'Enter' &&
                        handleOptionClick({ label, value, icon })
                    }}
                  >
                    {icon && (
                      <PadlockIcon
                        data-testid="label-icon"
                        className={styles.labelIcon}
                      />
                    )}
                    <TypographyV2
                      textStyle="sb-text-interface-body-small-regular"
                      {...(optionsHaveTracking && {
                        [tracking?.label]: `${tracking?.id}-option-${
                          index + 1
                        }`,
                      })}
                    >
                      {label}
                    </TypographyV2>
                  </li>
                )
              })}
            </ul>
          </div>
        </FocusTrap>
      )}
    </div>
  )
}

///////// IMPLEMENTATION /////////

export type CustomDropdownProps = {
  /**
   * CSS class name that will be added to the component wrapper.
   */
  className?: string
  /**
   * CSS class name that will be added to the visible selected menu item.
   */
  classNameSelectedContainer?: string
  /**
   * CSS class name that will be added to the dropdown component.
   */
  classNameOptionsContainer?: string
  /**
   * An array of menu options.
   */
  options: Option[]
  /**
   * A handler passed by the parent to update the sorting criteria.
   */
  handleSortByCallback: (options: Option) => void
  /**
   * A handler to open the Modal if user access is restricted.
   */
  handleOptionWithIconCallback?: (options: Option) => void

  handleOpenCloseCallback?: (options: { showOptions: boolean }) => void
  /**
   * Number specifying which value from the options array should be displayed as a default option. First value is 0.
   */
  defaultOption?: number
  /**
   * An optional object of strings specifying the tracking label and identifier, if tracking is required.
   */
  tracking?: {
    label: string
    id: string
  }
} & HTMLProps<HTMLDivElement>

type Option = { label: string; value: string; icon?: string }
